import { SparkleIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import type { WorkbookDefinition } from '@erp/shared-types';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ThinkingReasoning } from '@/components/common/chat/ThinkingReasoning';
import { useAgentState } from '@/context/agent-state.context';
import { getFriendlyToolName } from '@/lib/workflows/toolFriendlyNames';
import {
  generationActivity,
  isStepApplied,
  type GenerationActivity,
} from './generationActivity';
import { useWorkbookProcess } from './useWorkbookProcess';
import { useWorkbookToolLabel } from './useWorkbookToolLabel';
import { WorkbookFollowToggle } from './WorkbookFollow';

/** Generates one Instruction; "Regenerate" once it is applied and unchanged. */
export function WorkbookGenerateStepButton({
  id,
  step,
  variant = 'ghost',
}: {
  id: string;
  step: WorkbookDefinition['processSteps'][number];
  variant?: 'ghost' | 'outline';
}) {
  const { generation } = useWorkbookProcess();
  return (
    <Button
      size="sm"
      variant={variant}
      disabled={
        !generation.ready || generation.busy || !step.instructions.trim()
      }
      onClick={() => void generation.generate(id)}
    >
      <SparkleIcon />
      {isStepApplied(step) ? (
        <T context="workbook regenerate instruction action">Regenerate</T>
      ) : (
        <T context="workbook generate instruction action">Generate</T>
      )}
    </Button>
  );
}

/** Phases that say more than the tool being called. */
const NO_TOOL_LABEL: ReadonlySet<GenerationActivity> = new Set([
  'checking',
  'thinking',
  'fixing',
  'summarizing',
]);

/** The chat agent's thinking orb and live status line, for the Generation in flight. */
export function WorkbookGenerationStatus() {
  const gt = useGT();
  const { generation, definition } = useWorkbookProcess();
  const { activeToolCalls, streamingText } = useAgentState();
  const toolLabel = useWorkbookToolLabel(definition);
  const activity = generationActivity(
    activeToolCalls,
    streamingText,
    generation.checking,
  );
  const labels: Record<Exclude<GenerationActivity, object>, string> = {
    checking: gt('Checking whether this step is already up to date…', {
      context: 'workbook generation status: AI match check',
    }),
    thinking: gt('Thinking…', { context: 'workbook generation status' }),
    reading: gt('Reading the workbook…', {
      context: 'workbook generation status',
    }),
    'reading-file': gt('Reading the attached file…', {
      context: 'workbook generation status',
    }),
    'finding-data': gt('Looking up available data…', {
      context: 'workbook generation status',
    }),
    previewing: gt('Previewing source data…', {
      context: 'workbook generation status',
    }),
    trying: gt("Trying the change on the last run's data…", {
      context: 'workbook generation status: agent previews an unsaved edit',
    }),
    applying: gt('Applying and saving changes…', {
      context: 'workbook generation status',
    }),
    fixing: gt('Fixing problems in the changes…', {
      context: 'workbook generation status: agent fixes diagnostics',
    }),
    running: gt('Checking run results…', {
      context: 'workbook generation status',
    }),
    summarizing: gt('Writing a summary…', {
      context: 'workbook generation status',
    }),
  };
  const current = activeToolCalls.at(-1);
  // A tool's own label names its target; the phases without a tool keep theirs.
  const specific =
    current && !NO_TOOL_LABEL.has(activity) ? toolLabel(current) : null;
  const label =
    specific ??
    (typeof activity === 'object'
      ? (activity.tool.userFacingName ??
        getFriendlyToolName(activity.tool.toolName))
      : labels[activity]);
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div role="status" aria-live="polite">
        <ThinkingReasoning
          sentences={[label]}
          thinking
          elapsedSeconds={0}
          liveToolName={activeToolCalls.at(-1)?.toolName}
        />
      </div>
      <WorkbookFollowToggle />
    </div>
  );
}

/** Asks before regenerating an Instruction the AI check says is already up to date. */
export function WorkbookRegenerateDialog() {
  const gt = useGT();
  const { generation } = useWorkbookProcess();
  const { confirmation } = generation;
  return (
    <AlertDialog
      open={Boolean(confirmation)}
      onOpenChange={(open) => !open && generation.cancel()}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <T context="workbook regenerate likely match title">
              This step already looks up to date
            </T>
          </AlertDialogTitle>
          <AlertDialogDescription>
            {gt(
              'The AI check is {percent}% confident the workbook already does what this instruction says. Regenerate anyway?',
              {
                context: 'workbook regenerate likely match description',
                percent: Math.round((confirmation?.probability ?? 0) * 100),
              },
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>
            <T context="dialog cancel button">Cancel</T>
          </AlertDialogCancel>
          <AlertDialogAction onClick={() => void generation.confirm()}>
            <T context="workbook regenerate instruction action">Regenerate</T>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
